import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { segmentOf, TASK_SEGMENTS, TaskCard, type TaskSegment } from '../components/TaskCard';
import { ApprovalCard } from '../components/ui/ApprovalCard';
import { LiveMark } from '../components/ui/LiveMark';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { api, type TaskSummary } from '../lib/api';
import { useAgent } from '../lib/agent';
import type { ApprovalFrame } from '../lib/ws';
import type { ApprovalCardState } from '../components/ui/ApprovalCard';

type DemoBundle = {
  tasks: TaskSummary[];
  approval: Omit<ApprovalFrame, 'type'>;
  checklist: string[];
};

/** Loads sample data only when built with VITE_DEMO=true (the module is otherwise not bundled). */
function useDemo(lang: 'en' | 'ar'): DemoBundle | null {
  const [bundle, setBundle] = useState<DemoBundle | null>(null);
  useEffect(() => {
    // Inline env check so Vite folds the branch and never bundles the demo chunk in production.
    if (import.meta.env.VITE_DEMO !== 'true') return;
    let cancelled = false;
    void import('../lib/demo').then((d) => {
      if (cancelled) return;
      setBundle({
        tasks: d.demoTasks[lang],
        approval: d.demoApproval[lang],
        checklist: d.demoChecklist[lang],
      });
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);
  return bundle;
}

/** Loading state: the LiveMark thinking, never a "Loading…" string. */
function Thinking({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-2" role="status">
      <LiveMark state="thinking" size={16} title={label} />
    </div>
  );
}

/**
 * Tasks (M5.2): the four segments over real tasks only. Active shows active tasks and its own
 * empty state; what TWO has done lives under You → Activity, never here.
 */
export function TasksPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage === 'ar' ? 'ar' : 'en') as 'en' | 'ar';
  const [segment, setSegment] = useState<TaskSegment>('active');
  const [tasks, setTasks] = useState<TaskSummary[] | null>(null);
  const [approved, setApproved] = useState(false);
  const demo = useDemo(lang);
  const { approvalResults, approvals: liveApprovals } = useAgent();
  const [refreshKey, setRefreshKey] = useState(0);
  const [now] = useState(() => Date.now());
  const [cancelled, setCancelled] = useState<Record<string, string>>({});
  const cancelSchedule = async (scheduleId: string) => {
    try {
      const r = await api.cancelSchedule(scheduleId);
      setCancelled((c) => ({ ...c, [scheduleId]: r.line }));
    } catch {
      setCancelled((c) => ({ ...c, [scheduleId]: t('common.error') }));
    }
    setRefreshKey((k) => k + 1);
  };
  const resultCount = Object.keys(approvalResults).length;

  useEffect(() => {
    let cancelled = false;
    api
      .tasks()
      .then((a) => {
        if (!cancelled) setTasks(a.tasks);
      })
      .catch(() => {
        if (!cancelled) setTasks([]);
      });
    return () => {
      cancelled = true;
    };
    // A decision made from the conversation refreshes the rows here (resultCount).
  }, [refreshKey, resultCount]);

  const decide = async (task: TaskSummary, approval: ApprovalFrame, approve: boolean) => {
    if (approve) {
      await api.approveTask(task.task_id, {
        approval_id: approval.approval_id,
        signature: approval.signature,
      });
    } else {
      await api.cancelTask(task.task_id, { approval_id: approval.approval_id });
    }
    setRefreshKey((k) => k + 1);
  };
  const cardState = (approval: ApprovalFrame): ApprovalCardState => {
    const result = approvalResults[approval.approval_id];
    if (result) return result.status === 'approved' ? 'approved' : result.status;
    return new Date(approval.expires_at).getTime() < now ? 'expired' : 'pending';
  };

  // Real data first; demo data is appended only in demo builds.
  const allTasks = useMemo(() => [...(tasks ?? []), ...(demo?.tasks ?? [])], [tasks, demo]);
  const visible = allTasks.filter((task) => segmentOf(task.state) === segment);
  const counts = Object.fromEntries(
    (Object.keys(TASK_SEGMENTS) as TaskSegment[]).map((k) => [
      k,
      allTasks.filter((task) => segmentOf(task.state) === k).length,
    ]),
  ) as Record<TaskSegment, number>;
  const loading = tasks === null;

  return (
    <div className="scroller flex min-h-0 flex-1 flex-col gap-5 p-4">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-xl font-semibold tracking-tight">{t('nav.tasks')}</h2>
        <Link to="/you/activity" className="min-h-10 text-sm font-medium text-brand">
          {t('you.groups.done.title')}
        </Link>
      </header>

      <SegmentedControl<TaskSegment>
        label={t('tasks.segments')}
        value={segment}
        onChange={setSegment}
        options={(Object.keys(TASK_SEGMENTS) as TaskSegment[]).map((k) => ({
          value: k,
          label: counts[k] ? `${t(`tasks.segment.${k}`)} · ${counts[k]}` : t(`tasks.segment.${k}`),
        }))}
      />

      {loading ? (
        <Thinking label={t('common.loading')} />
      ) : visible.length === 0 ? (
        <div
          className="rounded-lg border border-line bg-surface p-6 text-center text-sm text-ink-2 dark:bg-elevated"
          role="status"
        >
          {allTasks.length === 0 ? t('tasks.emptyAll') : t(`tasks.empty.${segment}`)}
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {visible.map((task) => (
            <li key={task.task_id}>
              <TaskCard
                task={task}
                onCancelSchedule={task.schedule ? cancelSchedule : undefined}
                cancelledLine={task.schedule ? cancelled[task.schedule.schedule_id] : undefined}
              >
                {task.approval && segmentOf(task.state) === 'waiting' ? (
                  <ApprovalCard
                    approval={
                      liveApprovals.find((a) => a.approval_id === task.approval?.approval_id) ??
                      task.approval
                    }
                    state={cardState(task.approval)}
                    checklist={approvalResults[task.approval.approval_id]?.checklist ?? []}
                    onApprove={() => decide(task, task.approval as ApprovalFrame, true)}
                    onCancel={() => decide(task, task.approval as ApprovalFrame, false)}
                  />
                ) : demo && task.task_id === demo.approval.task_id ? (
                  <ApprovalCard
                    approval={demo.approval}
                    state={approved ? 'approved' : 'pending'}
                    checklist={demo.checklist}
                    onApprove={() => setApproved(true)}
                    onCancel={() => setApproved(false)}
                  />
                ) : null}
              </TaskCard>
            </li>
          ))}
        </ul>
      )}
      {demo ? <p className="text-center text-xs text-ink-3">{t('tasks.demoNote')}</p> : null}
    </div>
  );
}
